import React, { ComponentType } from 'react';
import { matchPath, RouteProps } from 'react-router-dom';
import makeLogger from 'shared/utils/makeLogger';

import DynamicImportFailed from '../components/DynamicImportFailed';

const logger = makeLogger(__filename, { shouldLog: true });

function handleDynamicImportError<T>(error: unknown): { default: T; } {
  // NOTE: the error isn't thrown
  logger.error('Error during dynamic import', { error });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- intentional fallback: DynamicImportFailed stands in for any component on import failure
  return { default: DynamicImportFailed as unknown as T }; // technically untrue
}

/**
 * Like `React.lazy()`, except:
 * 1. In the case of a dynamic import error, we silence it and return a fallback component.
 * 2. If the current (non-reactive) URL matches `matchingRouteProps` (i.e. when the site loads), we
 *   load the component immediately. This makes sure it's prioritized over other requests (e.g.
 *   service-worker precaching requests).
 * 3. It's a common for `matchingRouteProps` to match the props passed to a `<Route>`so we return an
 *   array containing the component and the `matchingRouteProps` (to make it easy to reuse them).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reactLazy<T extends ComponentType<any>>({
  importer,
  exportName = 'default',
  matchingRouteProps,
}: {
  importer: () => Promise<{ [name: string]: T | unknown; }>;
  exportName?: string;
  matchingRouteProps: RouteProps | 'there-isnt-a-matching-route-and-I-know-what-I-am-doing';
}): [React.LazyExoticComponent<T>, RouteProps] {
  if (matchingRouteProps && matchPath(window.location.pathname, matchingRouteProps)) {
    logger.info('Loading lazy component immediately', {
      exportName,
      importer,
      matchingRouteProps,
    });
    importer().catch(handleDynamicImportError);
  }

  const LazyComponent = React.lazy(async () => {
    try {
      const importResult = await importer();
      const Component = importResult[exportName];
      if (!Component) {
        return handleDynamicImportError<T>(new Error(`No such component (export): ${exportName}`));
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Component is T; TypeScript can't infer this from dynamic import indexing
      return { default: Component as T };
    } catch (error: unknown) {
      return handleDynamicImportError<T>(error);
    }
  });

  return [
    LazyComponent,
    typeof matchingRouteProps === 'string' ? {} : matchingRouteProps,
  ];
}
